import { useQuery } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ROLE_PERMISSIONS } from '../../../common/constants';
import { GET_MY_ROSTER_SHIFTS } from '../../../common/graphql/people/roster';
import { GET_PICKER_DAILY_STATS } from '../../../common/graphql/pickList';
import { GET_PICKING_WORK_FOR_DAY } from '../../../common/graphql/serviceDocket';
import { useUserContext } from '../../../common/utils/context';
import { getFullMachineName } from '../../../common/utils/machine';
import {
	getServiceDocketLabelType,
	isServiceDocketLabelPrintOnly,
} from '../../../common/utils/pickList/isServiceDocketLabelPrintOnly';
import { getLocalTime } from '../../../common/utils/time/time';
import { getUserName } from '../../../common/utils/user/getUserName';
import { formatDate } from '../../../common/utils/utils';
import createClientOperationId from '../../../common/utils/createClientOperationId';

import { useRefetchOnFocus, useRefreshPage } from '../../hooks';
import useWarehousePrintJob from '../picklist/useWarehousePrintJob';
import useMyClockStatus from './useMyClockStatus';

export const isRowPartiallyPicked = row => Boolean(row?.inventoryPartiallyPicked && !row?.inventoryPicked);
export const isRowLabelOnly = row => Boolean(row?.labelOnly);
const isRowCompletedForPicker = row => Boolean(!isRowLabelOnly(row) && (row?.inventoryPicked || isRowPartiallyPicked(row)));
const isRowPending = row => Boolean(!isRowLabelOnly(row) && !isRowCompletedForPicker(row));
export const isRowIncompletePick = row => Boolean(!row?.inventoryPicked && (
	row?.inventoryPartiallyPicked || row?.isAnyItemPicked
));
export const isRowAssignedToPicker = (row, pickerId) => Boolean(pickerId && row?.assignedPickers?.some(picker => picker.id === pickerId));

const getSortableTime = (value) => {
	const time = value ? new Date(value).getTime() : NaN;
	return Number.isFinite(time) ? time : Number.MAX_SAFE_INTEGER;
};

const getDocketOrder = (value) => {
	if (value === null || value === undefined || value === '') {
		return null;
	}
	const order = Number(value);
	return Number.isFinite(order) ? order : null;
};

const compareDocketOrder = (rowA, rowB) => {
	const orderA = getDocketOrder(rowA.order);
	const orderB = getDocketOrder(rowB.order);
	const hasOrderA = orderA !== null;
	const hasOrderB = orderB !== null;

	if (hasOrderA && hasOrderB && orderA !== orderB) {
		return orderA - orderB;
	}
	if (hasOrderA !== hasOrderB) {
		return hasOrderA ? -1 : 1;
	}
	return String(rowA.id).localeCompare(String(rowB.id));
};

const buildRouteGroups = ({ rows, rowFilter, timezone }) => {
	const groupsByServiceRunId = new Map();

	rows.forEach((row) => {
		const existingGroup = groupsByServiceRunId.get(row.serviceRunId);
		if (existingGroup) {
			existingGroup.rows.push(row);
			return;
		}

		groupsByServiceRunId.set(row.serviceRunId, {
			id: row.serviceRunId,
			routeName: row.routeName || 'Routeless run',
			planned: row.serviceRunPlanned,
			driverName: row.driverName,
			rows: [row],
		});
	});

	return Array.from(groupsByServiceRunId.values())
		.map((group) => {
			const filteredRows = group.rows.filter(rowFilter).sort(compareDocketOrder);
			const pickerNamesById = new Map();
			filteredRows.forEach((row) => {
				row.assignedPickers.forEach((picker) => {
					if (!pickerNamesById.has(picker.id)) {
						pickerNamesById.set(picker.id, picker.name);
					}
				});
			});

			return {
				...group,
				plannedLabel: group.planned
					? getLocalTime(timezone, group.planned).format('ddd D MMM YYYY · h:mm A')
					: 'Date not set',
				assignedPickers: Array.from(pickerNamesById, ([id, name]) => ({ id, name })),
				rows: filteredRows,
			};
		})
		.filter(group => group.rows.length > 0)
		.sort((groupA, groupB) => {
			const plannedDiff = getSortableTime(groupA.planned) - getSortableTime(groupB.planned);
			if (plannedDiff !== 0) {
				return plannedDiff;
			}
			return String(groupA.id).localeCompare(String(groupB.id));
		});
};

const filterRouteGroups = (routeGroups, searchValue) => {
	const normalizedQuery = searchValue.trim().toLowerCase();
	if (!normalizedQuery) {
		return routeGroups;
	}

	return routeGroups.filter((group) => {
		const searchableText = [
			group.routeName,
			group.plannedLabel,
			group.driverName,
			...group.assignedPickers.map(picker => picker.name),
			...group.rows.flatMap(row => [
				row.machineName,
				row.assetId,
				row.locationName,
				row.placeName,
			]),
		]
			.filter(Boolean)
			.join(' ')
			.toLowerCase();

		return searchableText.includes(normalizedQuery);
	});
};

/**
 * Today's picking work for the signed-in picker: data, Mine/All scope, search,
 * counts, clock gate and the label / tub sticker print requests. Shared by the
 * classic picker board (PickerMyDayView) and the redesigned Picking My Day.
 */
const usePickerMyDay = ({ refetchModeData } = {}) => {
	const navigation = useNavigation();
	const { tenant, user, isAVC, isPicker } = useUserContext();
	const [searchValue, setSearchValue] = useState('');
	const [labelRequest, setLabelRequest] = useState(null);
	const [completedPickRequest, setCompletedPickRequest] = useState(null);
	const triggeredLabelRequestRef = useRef(null);
	const timezone = tenant?.timezone;
	const {
		job: labelJob,
		isSubmitting: isSubmittingLabel,
		submitError: labelSubmitError,
		requestLabel,
		requestReprint: requestLabelReprint,
	} = useWarehousePrintJob({
		serviceDocketId: labelRequest?.row?.id,
		labelType: labelRequest?.row?.labelType,
		enabled: Boolean(labelRequest),
		alertOnError: false,
	});
	const {
		job: completedTubLabelJob,
		loading: isLoadingCompletedTubLabelJob,
		isSubmitting: isSubmittingCompletedTubLabel,
		submitError: completedTubLabelSubmitError,
		requestLabel: requestCompletedTubLabel,
		requestReprint: requestCompletedTubLabelReprint,
	} = useWarehousePrintJob({
		serviceDocketId: completedPickRequest?.row?.id,
		labelType: 'TUB',
		enabled: Boolean(completedPickRequest),
		alertOnError: false,
	});
	const printingLabelDocketId = isSubmittingLabel ? labelRequest?.row?.id : null;

	useEffect(() => {
		if (
			!labelRequest?.requestId ||
			triggeredLabelRequestRef.current === labelRequest.requestId
		) {
			return;
		}
		triggeredLabelRequestRef.current = labelRequest.requestId;
		requestLabel();
	}, [labelRequest?.requestId, requestLabel]);
	const {
		clockStatus,
		canUseClockStatus,
		isClockStatusReliable,
		refetch: refetchClockStatus,
	} = useMyClockStatus();
	const todayDateKey = useMemo(() => getLocalTime(timezone).format('YYYY-MM-DD'), [timezone]);

	const { data: rosterData, refetch: refetchRosterShifts } = useQuery(GET_MY_ROSTER_SHIFTS, {
		variables: {
			fromDate: todayDateKey,
			toDate: todayDateKey,
		},
		// Roster is AVC people-gated; errors must not block the board.
		skip: !isAVC,
		errorPolicy: 'all',
	});
	const rosterShift = useMemo(() => (
		(rosterData?.getMyRosterShifts || []).find(shift => shift?.date === todayDateKey) || null
	), [rosterData?.getMyRosterShifts, todayDateKey]);

	// Pickers process AVC driver returns; mirror the drawer's pending stock condition.
	const hasStockPagePermission = Boolean(user && (user.isSuperAdmin || user.role.permissions.includes(ROLE_PERMISSIONS.VIEW_STOCK_PAGE)));
	const canViewPendingStock = Boolean(hasStockPagePermission || isPicker);
	const todayDisplayLabel = useMemo(() => formatDate(getLocalTime(timezone), 'DAY_MONTHWORD_YEAR'), [timezone]);

	const { data, loading, error, refetch: refetchPickingWork } = useQuery(GET_PICKING_WORK_FOR_DAY, {
		variables: { date: todayDateKey },
		// Cached board stays usable offline; focus refetches keep it fresh, and
		// a failed refresh must not blank cached data.
		fetchPolicy: 'cache-and-network',
		errorPolicy: 'all',
	});
	useRefetchOnFocus(refetchPickingWork);

	const docketRows = useMemo(() => {
		const dockets = data?.getPickingWorkForDay || [];

		return dockets
			.filter(docket => (
				docket.serviceRun?.id &&
				(docket.pickList?.id || isServiceDocketLabelPrintOnly(docket))
			))
			.map(docket => {
				const { serviceRun } = docket;
				const planned = docket.planned || serviceRun.planned;
				const serviceRunPlanned = serviceRun.planned || docket.planned;
				const machineName = getFullMachineName(docket.machine, docket.machine.place, docket.machine.location);
				const labelOnly = isServiceDocketLabelPrintOnly(docket);
				const assignedPickersById = new Map();
				// Remaining work belongs only to the picker the planner assigned. pickList.pickers names
				// whoever ticked something on this stop, which may only credit stops already completed.
				const creditedPickers = isRowCompletedForPicker({ ...docket, labelOnly })
					? docket.pickList?.pickers || []
					: [];
				[docket.picker, ...creditedPickers].forEach((picker) => {
					if (picker?.id && !assignedPickersById.has(picker.id)) {
						assignedPickersById.set(picker.id, {
							id: picker.id,
							name: getUserName(picker) || 'Unnamed picker',
						});
					}
				});
				const assignedPickers = Array.from(assignedPickersById.values());
				const driverName = serviceRun.driver
					? [serviceRun.driver.firstname, serviceRun.driver.lastname].filter(Boolean).join(' ') ||
						serviceRun.driver.username ||
						'No driver assigned'
					: 'No driver assigned';
				return {
					id: docket.id,
					pickListId: docket.pickList?.id || null,
					labelOnly,
					labelType: labelOnly ? getServiceDocketLabelType(docket) : null,
					serviceRunId: serviceRun.id,
					machineName,
					assetId: docket.machine?.assetId,
					locationName: docket.machine?.location?.name,
					placeName: docket.machine?.place?.name,
					routeName: serviceRun.route?.name || serviceRun.name,
					planned,
					serviceRunPlanned,
					driverName,
					order: docket.order,
					status: docket.status,
					inventoryPicked: docket.inventoryPicked,
					inventoryPartiallyPicked: docket.inventoryPartiallyPicked,
					pickerName: assignedPickers[0]?.name || '',
					assignedPickers,
					packTags: docket.packTags || [],
					isAnyItemPicked: Boolean(docket.pickList?.isAnyItemPicked),
					hasProductSoldSinceLastPickListRecalculation: Boolean(docket.pickList?.hasProductSoldSinceLastPickListRecalculation),
				};
			});
	}, [data?.getPickingWorkForDay]);

	const routeGroups = useMemo(() => buildRouteGroups({
		rows: docketRows,
		rowFilter: row => !isRowCompletedForPicker(row),
		timezone,
	}), [docketRows, timezone]);
	const completedRouteGroups = useMemo(() => buildRouteGroups({
		rows: docketRows,
		rowFilter: isRowCompletedForPicker,
		timezone,
	}), [docketRows, timezone]);

	// Start on the picker's assignments even when none exist. The All view remains
	// an explicit opt-in for shared queue work.
	const [viewModeOverride, setViewModeOverride] = useState(null);
	const actionableDocketRows = useMemo(() => (
		docketRows.filter(row => !isRowCompletedForPicker(row))
	), [docketRows]);
	const myDocketRows = useMemo(() => (
		actionableDocketRows.filter(row => isRowAssignedToPicker(row, user?.id))
	), [actionableDocketRows, user?.id]);
	const myRouteGroups = useMemo(() => (
		routeGroups.filter(group => group.rows.some(row => isRowAssignedToPicker(row, user?.id)))
	), [routeGroups, user?.id]);
	const isMineMode = viewModeOverride !== 'all';
	const scopedRouteGroups = isMineMode ? myRouteGroups : routeGroups;
	const scopedCompletedRouteGroups = useMemo(() => (
		isMineMode
			? completedRouteGroups
				.map(group => ({
					...group,
					rows: group.rows.filter(row => isRowAssignedToPicker(row, user?.id)),
				}))
				.filter(group => group.rows.length > 0)
			: completedRouteGroups
	), [completedRouteGroups, isMineMode, user?.id]);
	const completedCount = useMemo(() => (
		scopedCompletedRouteGroups.reduce((count, group) => count + group.rows.length, 0)
	), [scopedCompletedRouteGroups]);

	const { data: statsData, refetch: refetchPickerStats } = useQuery(GET_PICKER_DAILY_STATS, {
		variables: { date: todayDateKey },
		// Stats are informational; failures must not block the board.
		errorPolicy: 'all',
	});
	const myStats = useMemo(() => (
		(statsData?.getPickerDailyStats || []).find(stat => stat?.picker?.id === user?.id) || null
	), [statsData?.getPickerDailyStats, user?.id]);
	const refetchMyDay = useCallback(() => {
		const requests = [refetchPickingWork(), refetchPickerStats()];
		if (isAVC) {
			requests.push(refetchRosterShifts(), refetchClockStatus());
		}
		if (refetchModeData) {
			requests.push(refetchModeData());
		}

		return Promise.all(requests);
	}, [
		isAVC,
		refetchClockStatus,
		refetchModeData,
		refetchPickerStats,
		refetchPickingWork,
		refetchRosterShifts,
	]);
	const [refreshPage, isRefreshing] = useRefreshPage(refetchMyDay);

	const filteredRouteGroups = useMemo(() => (
		filterRouteGroups(scopedRouteGroups, searchValue)
	), [scopedRouteGroups, searchValue]);
	const filteredCompletedRouteGroups = useMemo(() => (
		filterRouteGroups(scopedCompletedRouteGroups, searchValue)
	), [scopedCompletedRouteGroups, searchValue]);
	const filteredRows = useMemo(() => (
		filteredRouteGroups.flatMap(group => group.rows)
	), [filteredRouteGroups]);
	const pendingCount = useMemo(() => filteredRows.filter(isRowPending).length, [filteredRows]);
	const labelOnlyCount = useMemo(() => filteredRows.filter(isRowLabelOnly).length, [filteredRows]);

	const openClockPass = useCallback(() => {
		navigation.push('ClockPassView');
	}, [navigation]);

	const openPickingWork = useCallback(async (row) => {
		if (row?.labelOnly) {
			setLabelRequest({
				row,
				requestId: createClientOperationId('label-only-dialog'),
			});
			return;
		}

		if (!row?.pickListId) {
			return;
		}
		navigation.push('PickListDetailView', {
			id: row.pickListId,
			machineName: row.machineName,
			planned: row.planned,
			status: row.status,
			isAnyItemPicked: row.isAnyItemPicked,
			hasProductSoldSinceLastPickListRecalculation: row.hasProductSoldSinceLastPickListRecalculation,
			allowPartialCompletion: true,
		});
	}, [navigation]);
	const openCompletedPick = useCallback((row) => {
		setCompletedPickRequest({ row });
	}, []);
	const closeLabelRequest = useCallback(() => setLabelRequest(null), []);
	const closeCompletedPick = useCallback(() => setCompletedPickRequest(null), []);
	const showMine = useCallback(() => setViewModeOverride('mine'), []);
	const showAll = useCallback(() => setViewModeOverride('all'), []);

	return {
		userId: user?.id,
		todayDisplayLabel,
		// Only the true first load blanks the board; cache-and-network keeps
		// loading true during background refreshes over cached data.
		isFirstLoad: Boolean(loading && !data),
		hasData: Boolean(data),
		error,
		refreshPage,
		isRefreshing,
		// Clock
		canUseClockStatus,
		isClockStatusReliable,
		clockStatus,
		rosterShift,
		shouldHideWorkForClock: isClockStatusReliable && !clockStatus.isClockedIn,
		openClockPass,
		canViewPendingStock,
		// Scope, search and counts
		isMineMode,
		showMine,
		showAll,
		myCount: myDocketRows.length,
		allCount: actionableDocketRows.length,
		searchValue,
		setSearchValue,
		routeMachineCount: filteredRows.length,
		pendingCount,
		labelOnlyCount,
		completedCount,
		myStats,
		// Lists
		filteredRouteGroups,
		filteredCompletedRouteGroups,
		hasCompletedWork: scopedCompletedRouteGroups.length > 0,
		openPickingWork,
		openCompletedPick,
		printingLabelDocketId,
		// Print dialogs (PickerLabelDialogs)
		labelDialog: {
			isOpen: Boolean(labelRequest),
			onClose: closeLabelRequest,
			machineName: labelRequest?.row?.machineName || '',
			labelName: labelRequest?.row?.labelType === 'REPAIR' ? 'Repair-only label' : 'Packing label',
			job: labelJob,
			isSubmitting: isSubmittingLabel,
			error: labelSubmitError,
			onRetry: requestLabel,
			onReprint: requestLabelReprint,
		},
		completedPickDialog: {
			isOpen: Boolean(completedPickRequest),
			onClose: closeCompletedPick,
			machineName: completedPickRequest?.row?.machineName || '',
			job: completedTubLabelJob,
			isLoading: isLoadingCompletedTubLabelJob,
			isSubmitting: isSubmittingCompletedTubLabel,
			error: completedTubLabelSubmitError,
			onPrint: requestCompletedTubLabel,
			onReprint: requestCompletedTubLabelReprint,
		},
	};
};

export default usePickerMyDay;
